import React, { useContext, useEffect, useState } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'
import API from '../../../services/API'
import { GlobalContext } from '../../../App'
import LoadListSelect from './LoadListSelect'
import useLoadLists, { loadListsForKind } from './useLoadLists'

// Small "pick a load list for this vehicle/asset" dialog. Pulls the server's
// suggestion from /makeready/status so the same peer-based default the
// board offers shows up here too.
const AssignLoadListDialog = ({
  open,
  onClose,
  targetId,
  kind = 'asset',
  targetLabel,
  onAssigned,
}) => {
  const { setToast } = useContext(GlobalContext)
  const { loadLists } = useLoadLists({ enabled: open })
  const [suggested, setSuggested] = useState(null)
  const [selected, setSelected] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open || !targetId) return undefined
    let active = true
    setSelected('')
    setSuggested(null)
    API.get(`/makeready/status/${targetId}`)
      .then((res) => {
        if (!active || res.status !== 200) return
        setSuggested(res.data.suggestedLoadList || null)
        setSelected(
          res.data.loadList?._id || res.data.suggestedLoadList?._id || '',
        )
      })
      .catch(() => {})
    return () => {
      active = false
    }
  }, [open, targetId])

  useEffect(() => {
    if (!open || selected) return
    const options = loadListsForKind(loadLists, kind)
    if (options.length === 1) setSelected(options[0]._id)
  }, [open, loadLists, kind, selected])

  const handleAssign = () => {
    if (!selected || saving) return
    setSaving(true)
    API.post('/makeready/assign-load-list', {
      targetId,
      loadListId: selected,
    })
      .then((res) => {
        if (res.status === 200) {
          setToast({ message: 'Load list assigned' })
          onAssigned?.(res.data?.loadList || null)
          onClose()
        } else {
          setToast({
            message:
              res.data?.error ||
              'Unable to assign that load list. Please try again.',
            severity: 'error',
          })
        }
      })
      .catch(() =>
        setToast({
          message: 'Unable to assign that load list. Please try again.',
          severity: 'error',
        }),
      )
      .finally(() => setSaving(false))
  }

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>Assign load list</DialogTitle>
      <DialogContent>
        <Stack gap={1.5} sx={{ pt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Choose the load list {targetLabel ? `${targetLabel} ` : ''}should be
            checked against. You can change it later.
          </Typography>
          <LoadListSelect
            kind={kind}
            loadLists={loadLists}
            value={selected}
            onChange={(id) => setSelected(id || '')}
            suggestedLoadList={suggested}
            disabled={saving}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!selected || saving}
          onClick={handleAssign}
        >
          {saving ? 'Assigning…' : 'Assign'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AssignLoadListDialog
